import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Col, Form, Row, Space, Spin } from 'antd';
import { useLazyQuery } from '@apollo/client';
import {
  useReportValuesDispatchContext,
  useReportValuesStateContext
} from 'providers/ReportValues';
import { useAppDispatch } from 'store/hooks';
import { setTour } from 'store/tour/slice';
import { GET_DOCTOR_PROFILE } from 'gql/profile/queries';
import { DoctorById } from 'gql/profile/__generated__/DoctorById';
import { useAuthStateContext } from 'providers/Auth';

import { CKEditor } from 'components/shared';
import { Content } from './styled';
import { Loading } from 'components/ui';

interface IProps {
  loading: boolean;
  onSave: () => void;
  onSaveDraft: () => void;
  onClickBack: () => void;
}

const DoctorInfo: FC<IProps> = ({
  loading,
  onSave,
  onSaveDraft,
  onClickBack
}) => {
  const { doctor_info, lang } = useReportValuesStateContext();
  const [isLoading, setLoading] = useState(true);
  const { setValue } = useReportValuesDispatchContext();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { user } = useAuthStateContext();

  const [getProfile] = useLazyQuery<DoctorById>(GET_DOCTOR_PROFILE, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    onCompleted(data) {
      const value = data.doctorById.data.about || '';

      setValue('doctor_info', value);
      setLoading(false);
      setTimeout(() => {
        dispatch(setTour({ key: 'addDoctorReport', value: true }));
      });
    },
    variables: {
      lang,
      id: user?.id
    }
  });

  useEffect(() => {
    if (doctor_info) {
      setLoading(false);
      setTimeout(() => {
        dispatch(setTour({ key: 'addDoctorReport', value: true }));
      });
    } else {
      getProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTextChange = (value: string) => {
    setValue('doctor_info', value);
  };

  const onSubmit = (cb: () => void) => {
    if (doctor_info) {
      cb();
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Content>
      <div style={{ height: '100%', paddingBottom: 24 }}>
        <Form layout="vertical">
          <div>
            <Form.Item label={t('reports.about_doctor')}>
              <CKEditor
                onChange={handleTextChange}
                value={doctor_info || ''}
                toolbar={{
                  removeItems: [
                    'link',
                    'blockQuote',
                    'insertTable',
                    'mediaEmbed'
                  ]
                }}
              />
            </Form.Item>
          </div>
        </Form>
      </div>
      <Row justify="space-between" align="bottom">
        <Col>
          <Button onClick={onClickBack} disabled={loading}>
            {t('reports.back')}
          </Button>
        </Col>
        <Col>
          {loading ? (
            <Spin />
          ) : (
            <Space
              style={{
                paddingTop: 36,
                justifyContent: 'flex-end',
                width: '100%'
              }}
            >
              <Button
                ghost
                type="primary"
                disabled={!doctor_info}
                onClick={() => onSubmit(onSaveDraft)}
              >
                {t('reports.save')}
              </Button>
              <Button
                type="primary"
                disabled={!doctor_info}
                onClick={() => onSubmit(onSave)}
              >
                {t('reports.send')}
              </Button>
            </Space>
          )}
        </Col>
      </Row>
    </Content>
  );
};

export default DoctorInfo;
